import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { setGlobalToastManager } from '../auth/LogoutNotification'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  X, 
  AlertCircle,
  Loader2
} from 'lucide-react'
import { Button } from './Button'
import { cn } from '../../utils/cn'
import { useThemeStore } from '../../stores/theme'

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading'
export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

export interface Toast {
  id: string
  type: ToastType
  title?: string
  message: string
  duration?: number
  position?: ToastPosition
  action?: {
    label: string
    onClick: () => void
  }
  onClose?: () => void
  persistent?: boolean
  closable?: boolean
}

interface ToastState {
  toasts: Toast[]
}

type ToastAction =
  | { type: 'ADD_TOAST'; payload: Toast }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_TOASTS' }
  | { type: 'UPDATE_TOAST'; payload: { id: string; updates: Partial<Toast> } }

const toastReducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload]
      }
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      }
    case 'CLEAR_TOASTS':
      return {
        ...state,
        toasts: []
      }
    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map(toast =>
          toast.id === action.payload.id
            ? { ...toast, ...action.payload.updates }
            : toast
        )
      }
    default:
      return state
  }
}

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  updateToast: (id: string, updates: Partial<Toast>) => void
  clearToasts: () => void
  success: (message: string, options?: Partial<Toast>) => string
  error: (message: string, options?: Partial<Toast>) => string
  warning: (message: string, options?: Partial<Toast>) => string
  info: (message: string, options?: Partial<Toast>) => string
  loading: (message: string, options?: Partial<Toast>) => string
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] })

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const newToast: Toast = {
      id,
      duration: 5000,
      position: 'top-right',
      closable: true,
      ...toast
    }
    
    dispatch({ type: 'ADD_TOAST', payload: newToast })
    return id
  }, [])

  // Register global toast manager for use outside React components
  useEffect(() => {
    setGlobalToastManager({ addToast });
    return () => {
      setGlobalToastManager(null);
    };
  }, [addToast]);

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id })
  }, [])

  const updateToast = useCallback((id: string, updates: Partial<Toast>) => {
    dispatch({ type: 'UPDATE_TOAST', payload: { id, updates } })
  }, [])

  const clearToasts = useCallback(() => {
    dispatch({ type: 'CLEAR_TOASTS' })
  }, [])

  const success = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'success', message, ...options })
  }, [addToast])

  const error = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ 
      type: 'error', 
      message, 
      duration: 8000,
      persistent: true,
      ...options 
    })
  }, [addToast])

  const warning = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ 
      type: 'warning', 
      message, 
      duration: 6000,
      ...options 
    })
  }, [addToast])

  const info = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'info', message, ...options })
  }, [addToast])

  const loading = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ 
      type: 'loading', 
      message, 
      persistent: true,
      closable: false,
      ...options 
    })
  }, [addToast])

  const contextValue: ToastContextType = {
    toasts: state.toasts,
    addToast,
    removeToast,
    updateToast,
    clearToasts,
    success,
    error,
    warning,
    info,
    loading
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

function ToastContainer() {
  const { toasts } = useToast()
  const themeType = useThemeStore((s) => s.themeType)
  const isChildTheme = themeType === 'child'

  // Group toasts by position
  const toastsByPosition = toasts.reduce((acc, toast) => {
    const position = toast.position || 'top-right'
    if (!acc[position]) {
      acc[position] = []
    }
    acc[position].push(toast)
    return acc
  }, {} as Record<ToastPosition, Toast[]>)

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <ToastList
          key={position}
          toasts={positionToasts}
          position={position as ToastPosition}
          isChildTheme={isChildTheme}
        />
      ))}
    </div>
  )
}

interface ToastListProps {
  toasts: Toast[]
  position: ToastPosition
  isChildTheme: boolean
}

function ToastList({ toasts, position, isChildTheme }: ToastListProps) {
  const getPositionClasses = (position: ToastPosition) => {
    const positions = {
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4'
    }
    return positions[position]
}

  return (
    <div className={`absolute ${getPositionClasses(position)} space-y-2`}>
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            isChildTheme={isChildTheme}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

interface ToastItemProps {
  toast: Toast
  isChildTheme: boolean
}

function ToastItem({ toast, isChildTheme }: ToastItemProps) {
  const { removeToast } = useToast()

  useEffect(() => {
    if (toast.duration && !toast.persistent) {
      const timer = setTimeout(() => {
        removeToast(toast.id)
      }, toast.duration)

      return () => clearTimeout(timer)
    }
  }, [toast.duration, toast.persistent, toast.id, removeToast])

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />
      case 'loading':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />
    }
  }

  const getToastClasses = () => {
    const baseClasses = "pointer-events-auto max-w-sm w-full bg-white rounded-lg shadow-lg border"
    
    const typeClasses = {
      success: "border-green-200",
      error: "border-red-200",
      warning: "border-yellow-200",
      info: "border-blue-200",
      loading: "border-blue-200"
    }

    const themeClasses = isChildTheme 
      ? "bg-linear-to-r from-purple-50 to-pink-50" 
      : "bg-white"

    return cn(baseClasses, typeClasses[toast.type], themeClasses)
  }

  const handleClose = () => {
    if (toast.onClose) {
      toast.onClose()
    }
    removeToast(toast.id)
  }

  const handleAction = () => {
    if (toast.action?.onClick) {
      toast.action.onClick()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={getToastClasses()}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="shrink-0">
            {getIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            {toast.title && (
              <h4 className={cn(
                "text-sm font-medium mb-1",
                isChildTheme ? "text-child-primary font-child" : "text-therapist-primary font-therapist"
              )}>
                {toast.title}
              </h4>
            )}
            
            <p className={cn(
              "text-sm",
              isChildTheme ? "text-child-text" : "text-therapist-text"
            )}>
              {toast.message}
            </p>

            {toast.action && (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAction}
                  className="text-xs"
                >
                  {toast.action.label}
                </Button>
              </div>
            )}
          </div>

          {toast.closable && (
            <div className="shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

// Convenience hooks for common toast types
export function useSuccessToast() {
  const { success } = useToast()
  return success
}

export function useErrorToast() {
  const { error } = useToast()
  return error
}

export function useWarningToast() {
  const { warning } = useToast()
  return warning
}

export function useInfoToast() {
  const { info } = useToast()
  return info
}

export function useLoadingToast() {
  const { loading } = useToast()
  return loading
}